import { apiClient } from '@/lib/api';
import { ACTIVE_ORG_KEY, API_BASE_URL, TOKEN_KEY } from '@/lib/utils/constants';
import { leerPorPortal } from '@/lib/utils/portal-storage';
import { conSoloEjemplos } from '@/features/mini-prixia/solo-ejemplos';
import type { PaginaDelHistorial } from '@/types/historial.types';
import type {
  CreateRulePayload,
  PricingRule,
  ScopeCategory,
  ScopeProduct,
  UpdateRulePayload,
  BorradorRegla,
  CambioSinAplicar,
  ConteosDeReglas,
  EstadoDelRecalculo,
  RecalculoEnPantalla,
  VistaPreviaDelRecalculo,
  FilaSimulada,
  FiltroFoto,
  Foto,
  ListadoDeMarcas,
  ReglaRotaAbierta,
  ResultadoSimulacion,
  RevisionDePrecio,
  SimularBody,
} from '../types/pricing-rules.types';

// `apiClient` ya desenvuelve `data` y es genérico: `apiClient.get<T>()`, sin `as unknown as`.
export const pricingRulesApi = {
  // ── Rules ──
  /** Spec 294 (D-R4-4): cada regla COMO QUEDA, con su cambio sin aplicar y lo vigente. */
  list: (): Promise<PricingRule[]> => apiClient.get<PricingRule[]>('/api/pricing-rules'),

  /** Sólo lo que rige hoy, sin cambios sin aplicar (la ficha del producto, «Límites del producto»). */
  listVigentes: (): Promise<PricingRule[]> =>
    apiClient.get<PricingRule[]>('/api/pricing-rules?vigentes=1'),

  get: (id: string): Promise<PricingRule> => apiClient.get<PricingRule>(`/api/pricing-rules/${id}`),

  create: (payload: CreateRulePayload): Promise<PricingRule> =>
    apiClient.post<PricingRule>('/api/pricing-rules', payload),

  update: (id: string, payload: UpdateRulePayload): Promise<PricingRule> =>
    apiClient.patch<PricingRule>(`/api/pricing-rules/${id}`, payload),

  toggle: (id: string): Promise<PricingRule> =>
    apiClient.patch<PricingRule>(`/api/pricing-rules/${id}/toggle`),

  /**
   * Spec 294: una que rige queda «se borra al aplicar» (`seBorraAlAplicar: true`);
   * una nueva sin aplicar se descarta (false).
   */
  delete: (id: string): Promise<{ seBorraAlAplicar: boolean }> =>
    apiClient.delete<{ seBorraAlAplicar: boolean }>(`/api/pricing-rules/${id}`),

  /**
   * Spec 294 (D-R4-5): el historial de cambios de una regla, de a 20. `antesDe` =
   * el `siguiente` de la página anterior (null = la primera).
   */
  historial: (id: string, antesDe: number | null): Promise<PaginaDelHistorial> =>
    apiClient.get<PaginaDelHistorial>(
      `/api/pricing-rules/${id}/historial${antesDe != null ? `?antesDe=${antesDe}` : ''}`
    ),

  // ── Cambios sin aplicar (spec 294, D-R4-4) ──

  /** Los cambios sin aplicar de la empresa (con quién los escribió) y su firma. */
  cambiosSinAplicar: (): Promise<{ cambios: CambioSinAplicar[]; firma: string }> =>
    apiClient.get<{ cambios: CambioSinAplicar[]; firma: string }>('/api/pricing-rules/borradores'),

  /** «Descartar todos los cambios»: los de la empresa, de quien sea (permiso de encender). */
  descartarCambios: (): Promise<{ descartados: number }> =>
    apiClient.post<{ descartados: number }>('/api/pricing-rules/borradores/descartar'),

  /**
   * «Descartar y salir» (decisión del dueño, 7-oct): los que escribió sola la
   * persona real (los compartidos con otra quedan), de las `reglas` que se pasan
   * (ronda de la T11, I3: sólo las tocadas en esta visita, o las «de antes»). Con
   * cualquier permiso de cambiar reglas (crear, editar, encender o borrar).
   */
  descartarMios: (reglas: string[]): Promise<{ descartados: number }> =>
    apiClient.post<{ descartados: number }>('/api/pricing-rules/borradores/descartar-mios', {
      reglas,
    }),

  /**
   * Lo mismo al cerrar la ventana (`pagehide`): `fetch` con `keepalive`, que sigue
   * aunque la página se vaya y lleva el token (`sendBeacon` no puede mandar el
   * encabezado `Authorization`). No espera ni avisa: si no llega, al volver a
   * Reglas se ofrecen «de tu visita anterior».
   */
  descartarMiosAlCerrar: (reglas: string[]): void => {
    try {
      const token = leerPorPortal<string>(TOKEN_KEY);
      const org = leerPorPortal<string>(ACTIVE_ORG_KEY);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (org) headers['X-Organization-Id'] = org;
      void fetch(`${API_BASE_URL}/api/pricing-rules/borradores/descartar-mios`, {
        method: 'POST',
        keepalive: true,
        headers,
        body: JSON.stringify({ reglas }),
      }).catch(() => undefined);
    } catch {
      /* la página se está yendo: no hay a quién avisar */
    }
  },

  /**
   * «Descartar este cambio» (decisión del dueño, 7-oct): sólo el de esa regla.
   * Pide encender, o editar si el cambio lo escribió sólo esa persona.
   */
  descartarCambio: (id: string): Promise<{ descartados: number }> =>
    apiClient.delete<{ descartados: number }>(
      `/api/pricing-rules/${encodeURIComponent(id)}/borrador`
    ),

  /**
   * Con `paraElegir` (los asistentes de Reglas y Estrategias), en la demo sólo las de los productos
   * de ejemplo (343, S3.4). Sin él (filtrar «Cómo queda cada producto»), todas.
   */
  listCategories: (paraElegir = false): Promise<ScopeCategory[]> =>
    apiClient.get<ScopeCategory[]>(
      paraElegir
        ? conSoloEjemplos('/api/pricing-rules/scope/categories')
        : '/api/pricing-rules/scope/categories'
    ),

  /** Desde el 4B-datos (T6) trae también marca, precio, costo y margen. */
  searchProducts: async (q: string): Promise<ScopeProduct[]> => {
    if (q.trim().length < 2) return [];
    return apiClient.get<ScopeProduct[]>(
      conSoloEjemplos(`/api/pricing-rules/scope/products?q=${encodeURIComponent(q)}`)
    );
  },

  /** Spec 294 (D-R4-6): sin el mínimo de 2 letras; sin texto, los primeros activos por nombre. */
  productosParaElegir: (q: string): Promise<ScopeProduct[]> =>
    apiClient.get<ScopeProduct[]>(
      conSoloEjemplos(`/api/pricing-rules/scope/products?q=${encodeURIComponent(q.trim())}`)
    ),

  /** Los elegidos por su código, para mostrarlos con su nombre (también inactivos). */
  productosPorCodigo: (skus: string[]): Promise<ScopeProduct[]> =>
    skus.length === 0
      ? Promise.resolve([])
      : apiClient.get<ScopeProduct[]>(
          `/api/pricing-rules/scope/products?skus=${encodeURIComponent(skus.join(','))}`
        ),

  // ── «Aplicar las reglas y recalcular» (lote C1, punto 8) ──

  /** Calcula sin escribir nada (alcanza con ver reglas). */
  vistaPreviaDelRecalculo: (): Promise<VistaPreviaDelRecalculo> =>
    apiClient.post<VistaPreviaDelRecalculo>(
      '/api/pricing-rules/recalculo/vista-previa',
      {},
      {
        timeout: 120000,
      }
    ),

  /**
   * Aplica los cambios sin aplicar y lo manda a Por decidir. Con otro en curso, el
   * 409 trae su id en `data.id`. Spec 294 (D-R4-4): la firma de la vista previa va
   * SIEMPRE, también vacía (`''` = «no había cambios»): el servidor la compara y,
   * si alguien escribió un cambio mientras se miraba, responde 409 sin aplicar nada.
   */
  mandarRecalculo: (firmaDeBorradores: string): Promise<{ id: string; aplicados?: number }> =>
    apiClient.post<{ id: string; aplicados?: number }>('/api/pricing-rules/recalculo', {
      firmaDeBorradores,
    }),

  recalculo: (id: string): Promise<RecalculoEnPantalla> =>
    apiClient.get<RecalculoEnPantalla>(`/api/pricing-rules/recalculo/${id}`),

  estadoDelRecalculo: (): Promise<EstadoDelRecalculo> =>
    apiClient.get<EstadoDelRecalculo>('/api/pricing-rules/recalculo/estado'),

  // ── Motor de rangos (plan 4A) y plan 4B-datos ──

  /** Como `listCategories`: con `paraElegir`, en la demo sólo las marcas de los ejemplos. */
  listBrands: (paraElegir = false): Promise<ListadoDeMarcas> =>
    apiClient.get<ListadoDeMarcas>(
      paraElegir
        ? conSoloEjemplos('/api/pricing-rules/scope/brands')
        : '/api/pricing-rules/scope/brands'
    ),

  /** Pasa por el limitador de escrituras (120/min): la pantalla espera ≥ 400 ms después de tipear. */
  simular: (body: SimularBody, paraElegir = false): Promise<ResultadoSimulacion> =>
    apiClient.post<ResultadoSimulacion>(
      // Con `paraElegir` (la lista de las excepciones), en la demo sólo los de ejemplo. El conteo
      // «Alcanza N» NO lo pasa: lo decide el servidor.
      paraElegir ? conSoloEjemplos('/api/pricing-rules/simular') : '/api/pricing-rules/simular',
      body
    ),

  explicar: (body: {
    productId: string;
    borrador?: BorradorRegla;
    reemplaza?: string;
  }): Promise<FilaSimulada> => apiClient.post<FilaSimulada>('/api/pricing-rules/explicar', body),

  /**
   * `editado` (Task 0, ajuste 4): el costo, la categoría y la marca que se están
   * editando. Sin la clave, el backend usa lo guardado. Así el aviso dice lo mismo
   * que va a pasar al guardar.
   */
  revisarPrecio: (
    productId: string,
    precio: number,
    editado: { costo?: number | null; category?: string | null; brand?: string | null } = {},
    // Lote B2: el límite que se va a agregar sólo para este producto, para decir qué cambia.
    borrador?: BorradorRegla,
    // Auditoría H8: la regla de este producto que el borrador reemplaza (se edita, no se suma).
    reemplaza?: string
  ): Promise<RevisionDePrecio> =>
    apiClient.post<RevisionDePrecio>('/api/pricing-rules/revisar-precio', {
      productId,
      precio,
      ...editado,
      ...(borrador ? { borrador } : {}),
      ...(borrador && reemplaza ? { reemplaza } : {}),
    }),

  conteos: (dias = 7): Promise<ConteosDeReglas> =>
    apiClient.get<ConteosDeReglas>(`/api/pricing-rules/conteos?dias=${dias}`),

  foto: (f: FiltroFoto): Promise<Foto> => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
      if (v === undefined || v === null || v === '') continue;
      q.set(k, String(v));
    }
    return apiClient.get<Foto>(`/api/pricing-rules/foto?${q.toString()}`);
  },

  recalcularFoto: (): Promise<{ recalculando: true }> =>
    apiClient.post<{ recalculando: true }>('/api/pricing-rules/foto/recalcular'),

  reglasRotas: (productId: string): Promise<{ abiertas: ReglaRotaAbierta[] }> =>
    apiClient.get<{ abiertas: ReglaRotaAbierta[] }>(
      `/api/pricing-rules/reglas-rotas?productId=${encodeURIComponent(productId)}`
    ),
};
